import React, { useContext, useEffect, useState } from 'react';
import { Context } from '../../../context';
import './login.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from '../../../customHooks/useForm';
import useFetch from '../../../customHooks/useFetch';
import ParentLogo from '../../../assets/images/EZP_logo_8[22].png';
import SpanishLogo from '../../../assets/images/EZP_logo_ES3.png';
import TrainingLogo from '../../../assets/images/training_logo.png';
import LoginHeader from './LoginHeader';
import useStore from '../../../customHooks/useStore';
import { completeAdminLogin } from './adminLogin';
import AuthPortalSwitcher, {
  FACILITATOR_PORTAL,
  PARENT_PORTAL,
  getAuthPortal,
} from './AuthPortalSwitcher';
import {
  ROLE_PARENT,
  isAdminRole,
  normalizeRole,
  isParentRole,
  setStoredRole,
} from '../../../utils/authRole';

const TECH_SUPPORT_URL =
  'https://osu.az1.qualtrics.com/jfe/form/SV_eqy7DOElHo3rYh0';

function Login() {
  const { language } = useStore((state) => ({
    language: state.language,
  }));
  const location = useLocation();
  const pathPortal =
    location.pathname === '/facilitator/login'
      ? FACILITATOR_PORTAL
      : PARENT_PORTAL;
  const portal = getAuthPortal(location, pathPortal);
  const isFacilitatorPortal = portal === FACILITATOR_PORTAL;
  const authHelperCopy = isFacilitatorPortal
    ? 'If you are a parent, please toggle to the parent sign in page before signing in.'
    : 'If you are a facilitator or admin, please toggle to the facilitator sign in page before signing in.';
  const signUpPath = `/sign-up?role=${portal}`;
  const resetPasswordPath = `/reset-password?role=${portal}`;
  const [formState, handleChange, setState] = useForm();
  const [error, setError] = useState('');
  const history = useHistory();
  const [res, callAPI] = useFetch({
    url: 'login',
    method: 'POST',
    payload: formState,
    responseFormat: 'JSON',
  });
  const context = useContext(Context);
  useEffect(() => {
    if (res.error) {
      setState({});
      setError("The username or password you've entered is incorrect");
    }
    if (res.data) {
      const normalizedRole = normalizeRole(res.data.role);
      const initialVideo = res.data.initialVideo ?? false;

      if (isParentRole(normalizedRole) || isAdminRole(normalizedRole)) {
        localStorage.setItem('name', res.data.name);
        localStorage.setItem('initialVideo', String(initialVideo));
        if (isAdminRole(normalizedRole)) {
          completeAdminLogin({
            context,
            history,
            token: res.data.token,
            name: res.data.name,
            initialVideo,
            showExplain: res.data.showExplain,
            program: res.data.program,
            role: normalizedRole,
          });
        } else {
          setStoredRole(ROLE_PARENT);
          context.setIsAdmin(false, null);
          context.login(res.data.token, true);
          history.push('/course/overview');
        }
      } else {
        setState({});
        setError('Unable to determine the account role for this login.');
      }
    }
  }, [context, history, res, setState]);

  const submitLogin = (e) => {
    e.preventDefault();

    callAPI();
  };
  return (
    <div className='auth-wrapper'>
      <LoginHeader
        isAFacilitator={isFacilitatorPortal}
        logedInOrSignUp='login'
        signUpPath={signUpPath}
        resetPasswordPath={resetPasswordPath}
      />
      <section>
        <img
          src={
            isFacilitatorPortal
              ? TrainingLogo
              : language === 'es'
                ? SpanishLogo
                : ParentLogo
          }
          alt={isFacilitatorPortal ? 'ezTraining' : 'ezParent'}
          className='logo'
        />
        <AuthPortalSwitcher portal={portal} />
        <div className='login-wrap'>
          <div className='inner-wrap'>
            <h1>
              {isFacilitatorPortal ? 'Facilitator Sign In' : 'Parent Sign In'}
            </h1>
            <p className='auth-helper-copy'>{authHelperCopy}</p>
            <form onSubmit={(e) => submitLogin(e)}>
              <label>
                <p>Username</p>
                <input
                  required
                  name='username'
                  type='text'
                  value={formState['username'] || ''}
                  onChange={(e) => {
                    handleChange(e);
                    setError('');
                  }}
                />
              </label>
              <label>
                <p>Password</p>
                <input
                  required
                  value={formState['password'] || ''}
                  onChange={(e) => {
                    handleChange(e);
                    setError('');
                  }}
                  name='password'
                  type='password'
                />
              </label>
              {error && <p className='error'>{error}</p>}
              <div className='button-wrap'>
                <button
                  className={`button ${res.loading ? 'disabled' : ''}`}
                  type='submit'
                  disabled={res.loading}
                >
                  {res.loading ? 'Signing In...' : 'Sign In'}
                </button>
              </div>
              <a
                href={TECH_SUPPORT_URL}
                target='_blank'
                rel='noreferrer'
                className='tech-support-link'
              >
                Need Tech Support
              </a>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Login;
